/* ==========================================================================
   Bloomkind theme — collection / search filtering
   Re-renders the results section through the Section Rendering API so filters
   and sorting update without a full page load. Falls back to normal form
   submission (and links) without JavaScript.
   ========================================================================== */
(() => {
  'use strict';
  if (customElements.get('collection-facets')) return;

  const cache = new Map();

  class CollectionFacets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.form = this.querySelector('form');
      if (!this.form) return;
      const debounced = window.Bloomkind.debounce(() => this.apply(), 450);
      this.addEventListener('change', (event) => {
        const el = event.target;
        if (el.hasAttribute('data-name')) this.mirror(el);
        if (el.type === 'number') debounced(); else this.apply();
      });
      this.addEventListener('input', (event) => {
        if (event.target.type === 'number' && event.target.hasAttribute('data-name')) this.mirror(event.target);
      });
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.apply(); });
      this.addEventListener('click', (event) => {
        const link = event.target.closest('.facets__active a');
        if (!link) return;
        event.preventDefault();
        this.render(new URL(link.href, window.location.origin).searchParams.toString());
      });
      if (!CollectionFacets.outsideBound) {
        CollectionFacets.outsideBound = true;
        document.addEventListener('click', (event) => {
          document.querySelectorAll('.facet-pop[open]').forEach((d) => { if (!d.contains(event.target)) d.open = false; });
        });
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') document.querySelectorAll('.facet-pop[open]').forEach((d) => { d.open = false; });
        });
      }
      if (!CollectionFacets.popBound) {
        CollectionFacets.popBound = true;
        window.addEventListener('popstate', () => {
          const el = document.querySelector('collection-facets');
          if (el) el.render(window.location.search.slice(1), false);
        });
      }
    }

    /* Copy a mobile (mirror) input's state into the real form input. */
    mirror(source) {
      const name = source.dataset.name;
      const selector = source.type === 'checkbox'
        ? `[name="${CSS.escape(name)}"][value="${CSS.escape(source.value)}"]`
        : `[name="${CSS.escape(name)}"]`;
      const target = this.form.querySelector(selector);
      if (!target) return;
      if (source.type === 'checkbox') target.checked = source.checked;
      else target.value = source.value;
    }

    params() {
      const data = new FormData(this.form);
      const params = new URLSearchParams();
      for (const [key, value] of data.entries()) if (value !== '') params.append(key, value);
      return params.toString();
    }

    apply() { this.render(this.params()); }

    async render(query, push = true) {
      const results = document.getElementById(`ProductGrid-${this.sectionId}`);
      if (results) results.classList.add('is-loading');
      const url = `${window.location.pathname}?${query}${query ? '&' : ''}section_id=${this.sectionId}`;
      try {
        let html = cache.get(url);
        if (!html) {
          const response = await fetch(url);
          html = await response.text();
          cache.set(url, html);
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshResults = doc.getElementById(`ProductGrid-${this.sectionId}`);
        const freshFacets = doc.querySelector('collection-facets');
        const drawer = this.querySelector('bk-drawer');
        const drawerOpen = drawer && drawer.classList.contains('is-open');
        const openPops = Array.from(this.querySelectorAll('.facet-pop')).map((d, i) => (d.open ? i : -1)).filter((i) => i > -1);
        if (freshResults && results) results.replaceWith(freshResults);
        if (freshFacets) {
          // Keep the open drawer in place; refresh the form and the drawer body separately.
          const newForm = freshFacets.querySelector('form');
          if (newForm) this.form.replaceWith(newForm);
          this.form = this.querySelector('form');
          this.form.addEventListener('submit', (event) => { event.preventDefault(); this.apply(); });
          const newBody = freshFacets.querySelector('bk-drawer .drawer__body');
          const body = drawer && drawer.querySelector('.drawer__body');
          if (newBody && body) body.replaceWith(newBody);
          const newFoot = freshFacets.querySelector('bk-drawer .drawer__foot');
          const foot = drawer && drawer.querySelector('.drawer__foot');
          if (newFoot && foot) foot.replaceWith(newFoot);
          if (drawer && drawerOpen) drawer.classList.add('is-open');
          const pops = this.querySelectorAll('.facet-pop');
          openPops.forEach((i) => { if (pops[i]) pops[i].open = true; });
        }
        if (push) window.history.pushState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
        const count = this.querySelector('[data-count]');
        if (count) window.Bloomkind.announce(count.textContent.trim());
      } catch (error) {
        window.location.search = query;
      }
    }
  }
  customElements.define('collection-facets', CollectionFacets);
})();
